import { DeleteOutlined } from '@ant-design/icons';
import { Avatar, Form } from 'antd';
import Box from 'components/Box';
import Button from 'components/Button';
import CurrencyInputPrecision from 'components/CurrencyInputPrecision';
import {
  ButtonFooter,
  GroupButtonFooterModalStyled,
} from 'components/GroupButtonFooterModal';
import Modal from 'components/Modal';
import useModalConfirm from 'components/Modal/useModalConfirm';
import { CurrencyKeyBoardRef } from 'components/NumberKeyBoard/Currency';
import Text from 'components/Text';
import ticketListActions from 'features/ticketList/services/actions';
import ticketListSelectors from 'features/ticketList/services/selectors';
import { IApiSplitTicketItem } from 'features/ticketList/services/types/ticket';
import { sumBy } from 'lodash';
import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useSetLoadingPage } from 'services/UI/LoadingPage';
import { IStaffItemData } from 'services/shop/types/staff';
import { useAppDispatch } from 'store/hooks';
import styled from 'styled-components';
import { formatCurrency } from 'utils/formatCurrency';
import { roundToDecimal } from 'utils/roundToDecimal';
import toast from 'utils/toast';
import ModalChooseStaffFilter from 'widgets/Staff/DropDown/ModalChooseStaffFilter';
import {
  addLock,
  CategoryType,
  LockedField,
  rebalanceAllCategories,
  removeAllLocksForStaff,
  removeLock,
  updateLockIndicesAfterRemove,
} from './autoBalanceHelpers';

// Constants
const CATEGORY_NAMES = {
  amount: 'Sub-total',
  tip: 'tip',
  discount: 'discount',
  supplyFee: 'supply Fee',
} as const;

const RESET_MESSAGE = 'Are you sure you want to reset all changes? This action cannot be undone.';
const RESET_SUCCESS_MESSAGE = 'Reset successful';
const RESET_ERROR_MESSAGE = 'Reset failed. Please try again.';

// Types
type Ref = {
  open: () => void;
};

type Props = {
  v2?: boolean;
};

type FormValues = { staffs: IApiSplitTicketItem[] };
export const useModalSplitTicketRef = () => useRef<Ref>(null);
export const ModalSplitTicket = forwardRef<Ref, Props>(({ v2 }, ref) => {
  const dispatch = useAppDispatch();
  const currencyRef = useRef<CurrencyKeyBoardRef>(null);
  const details = ticketListSelectors.ticketDetailsData.data();
  const [visible, setVisible] = useState(false);
  const [visibleChooseStaff, setVisibleChooseStaff] = useState(false);
  const [form] = Form.useForm<{ staffs: IApiSplitTicketItem[] }>();
  const staffs = Form.useWatch('staffs', form);
  const setPageLoading = useSetLoadingPage();

  // Modal confirm for reset
  const [modalConfirmRef, modalConfirm] = useModalConfirm();

  // Auto-balance state management
  const staffsRef = useRef<IApiSplitTicketItem[]>([]);
  const lockedFields = useRef<LockedField[]>([]);
  const draftLockedField = useRef<LockedField | null>(null);
  const isUserEditingRef = useRef(false);

  const addRef = useRef<(defaultValue?: IApiSplitTicketItem) => void>(
    () => undefined
  );

  const filterStaff = useCallback(
    (list: IStaffItemData[]) => {
      return list.filter(
        (o) => !o.anyBody && staffs?.findIndex((s) => o.id === s.staffId) === -1
      );
    },
    [staffs]
  );

  /**
   * Get category totals for auto-balance calculations
   */
  const getCategoryTotals = useCallback(() => {
    return {
      amount: roundToDecimal(details.subTotal),
      tip: roundToDecimal(details.tip),
      discount: roundToDecimal(details.discountTicket),
      supplyFee: roundToDecimal(details.supplyFee || 0),
    };
  }, [details]);

  /**
   * Handle field value changes and prepare for auto-balance
   */
  const handleFieldChange = useCallback(
    (staffIndex: number, category: CategoryType, value: number) => {
      if (!staffsRef.current) return;

      isUserEditingRef.current = true;

      // Only trigger auto-balance if value actually changed
      const currentValue = staffsRef.current[staffIndex]?.[category] || 0;
      if (roundToDecimal(currentValue) === roundToDecimal(value)) {
        draftLockedField.current = null;
        return;
      }

      // Check if input value exceeds category total
      const totals = getCategoryTotals();
      const totalLockedFields = lockedFields.current
        .filter((field) => field.category === category)
        .reduce((sum, field) => {
          return sum + roundToDecimal(field.value);
        }, 0);
      
      if (roundToDecimal(totals[category]) - totalLockedFields < value) {
        draftLockedField.current = null;
        form.setFieldValue('staffs', staffsRef.current);
        return;
      }
      
      draftLockedField.current = { staffIndex, category, value };
    },
    [getCategoryTotals, lockedFields, form]
  );

  /**
   * Handle adding new staff with auto-balance
   */
  const handleAddStaffAutoBalance = useCallback(
    (newStaff: IApiSplitTicketItem) => {
      if (!staffsRef.current || isUserEditingRef.current) return;

      const newStaffs = [...staffsRef.current, newStaff];
      const totals = getCategoryTotals();

      // Reset locks for new staff (last index)
      lockedFields.current = removeAllLocksForStaff(
        lockedFields.current,
        newStaffs.length - 1
      );

      // Rebalance all categories
      const balancedStaffs = rebalanceAllCategories(
        newStaffs,
        totals,
        lockedFields.current
      );
      form.setFieldValue('staffs', balancedStaffs);
      staffsRef.current = balancedStaffs;
    },
    [getCategoryTotals, form]
  );

  /**
   * Handle removing staff with auto-balance
   */
  const handleRemoveStaffAutoBalance = useCallback(
    (staffIndex: number) => {
      if (!staffsRef.current || isUserEditingRef.current) return;

      const newStaffs = staffsRef.current.filter((_, index) => index !== staffIndex);
      const totals = getCategoryTotals();

      // Update lock indices after removal
      lockedFields.current = updateLockIndicesAfterRemove(
        lockedFields.current,
        staffIndex
      );

      // Rebalance all categories with updated locks
      const balancedStaffs = rebalanceAllCategories(
        newStaffs,
        totals,
        lockedFields.current
      );

      form.setFieldValue('staffs', balancedStaffs);
      staffsRef.current = balancedStaffs;
    },
    [getCategoryTotals, form]
  );

  /**
   * Create new staff and add with auto-balance
   */
  const handleAddStaff = (o: IStaffItemData) => {
    const newStaff: IApiSplitTicketItem = {
      staffId: o.id,
      amount: 0,
      avatar: o.urlImage,
      staffName: o.nickName ?? o.firstName ?? '',
      discount: 0,
      supplyFee: 0,
      tip: 0,
    };

    handleAddStaffAutoBalance(newStaff);
  };

  /**
   * Handle field focus - remove lock to allow editing
   */
  const handleFocus = useCallback(
    (staffIndex: number, category: CategoryType) => {
      isUserEditingRef.current = true;
      lockedFields.current = removeLock(
        lockedFields.current,
        staffIndex,
        category
      );
    },
    []
  );

  /**
   * Handle field blur - trigger auto-balance with smart locking logic
   */
  const handleBlur = useCallback(
    (category: CategoryType) => {
      isUserEditingRef.current = false;
      if (!staffsRef.current || !staffs) return;
      
      const categoryLocks = lockedFields.current.filter((field) => field.category === category);
      
      // If all fields in category are locked, do nothing
      if (categoryLocks.length === staffs.length) {
        return;
      }

      const totals = getCategoryTotals();
      
      if (draftLockedField.current) {
        // Check if current field is the last unlocked field
        const unlockedFields = staffs
          .map((_, index) => index)
          .filter(index => !categoryLocks.some(lock => lock.staffIndex === index));
        
        const isLastUnlockedField = unlockedFields.length === 1 && 
          unlockedFields[0] === draftLockedField.current.staffIndex;
        
        if (isLastUnlockedField) {
          // If it's the last unlocked field, only rebalance without adding lock
          const balancedStaffs = rebalanceAllCategories(
            staffs,
            totals,
            lockedFields.current
          );
          form.setFieldValue('staffs', balancedStaffs);
          staffsRef.current = balancedStaffs;
        } else {
          // If not the last field, add lock and rebalance
          const updatedLocks = addLock(
            lockedFields.current,
            draftLockedField.current.staffIndex,
            draftLockedField.current.category,
            draftLockedField.current.value
          );
          lockedFields.current = updatedLocks;
          const balancedStaffs = rebalanceAllCategories(
            staffs,
            totals,
            updatedLocks
          );
          form.setFieldValue('staffs', balancedStaffs);
          staffsRef.current = balancedStaffs;
        }
      } else {
        // No draft lock, just rebalance
        const balancedStaffs = rebalanceAllCategories(
          staffs,
          totals,
          lockedFields.current
        );
        form.setFieldValue('staffs', balancedStaffs);
        staffsRef.current = balancedStaffs;
      }
      draftLockedField.current = null;
    },
    [staffs, getCategoryTotals, form]
  );

  const handleClose = () => setVisible(false);

  const handleReset = useCallback(() => {
    modalConfirmRef.current?.open(
      {
        message: RESET_MESSAGE,
        okTitle: 'Reset',
        cancelTitle: 'Cancel',
      },
      () => {
        try {
          // Reset all state
          lockedFields.current = [];
          draftLockedField.current = null;
          isUserEditingRef.current = false;

          // Create initial dataList
          const dataList = (details?.items || []).map(
            (o) =>
              ({
                staffId: o.staffId,
                amount: 0,
                avatar: o.avatar,
                staffName: o.staffName,
                discount: 0,
                supplyFee: 0,
                tip: 0,
              } as IApiSplitTicketItem)
          );

          // Rebalance all categories with new data
          const totals = {
            amount: roundToDecimal(details.subTotal),
            tip: roundToDecimal(details.tip),
            discount: roundToDecimal(details.discountTicket),
            supplyFee: roundToDecimal(details.supplyFee || 0),
          };

          const balancedDataList = rebalanceAllCategories(dataList, totals, []);
          form.setFieldValue('staffs', balancedDataList);
          staffsRef.current = balancedDataList;

          toast.success(RESET_SUCCESS_MESSAGE);
        } catch (error) {
          console.error('Error in handleReset:', error);
          toast.error(RESET_ERROR_MESSAGE);
        }
      }
    );
  }, [details, form, modalConfirmRef]);

  const handleFinish = async (values: FormValues) => {
    handleClose();
    setPageLoading(true);
    const payload: IApiSplitTicketItem[] = values.staffs.map(
      (o) =>
        ({
          amount: o.amount,
          discount: o.discount,
          staffId: o.staffId,
          supplyFee: o.supplyFee,
          tip: o.tip,
        } as IApiSplitTicketItem)
    );
    dispatch(
      ticketListActions.splitCloseTicket({
        billId: details.billId,
        data: payload,
      })
    );
  };


  const checkValid = (staffs: IApiSplitTicketItem[]) => {
    const subTotal = roundToDecimal(sumBy(staffs, (o) => o.amount));
    const tip = roundToDecimal(sumBy(staffs, (o) => o.tip));
    const discount = roundToDecimal(sumBy(staffs, (o) => o.discount));
    const supplyFee = roundToDecimal(sumBy(staffs, (o) => o.supplyFee));

    // Check if all values are zero
    if (!subTotal && !tip && !discount && !supplyFee) {
      return { error: '', valid: false };
    }

    // Validate each category
    const validations = [
      {
        value: subTotal,
        expected: roundToDecimal(details.subTotal),
        name: CATEGORY_NAMES.amount,
      },
      {
        value: tip,
        expected: roundToDecimal(details.tip),
        name: CATEGORY_NAMES.tip,
      },
      {
        value: discount,
        expected: roundToDecimal(details.discountTicket),
        name: CATEGORY_NAMES.discount,
      },
      {
        value: supplyFee,
        expected: roundToDecimal(details.supplyFee || 0),
        name: CATEGORY_NAMES.supplyFee,
      },
    ];

    // Check for values exceeding totals
    for (const validation of validations) {
      if (validation.value > validation.expected) {
        return {
          error: `The ${validation.name} does not match`,
          valid: false,
        };
      }
    }

    // Check for exact matches
    for (const validation of validations) {
      if (validation.value !== validation.expected) {
        return {
          error: `The ${validation.name} does not match`,
          valid: false,
        };
      }
    }

    return { error: '', valid: true };
  };

  useImperativeHandle(ref, () => ({
    open() {
      const dataList = (details?.items || []).map(
        (o) =>
          ({
            staffId: o.staffId,
            amount: 0,
            avatar: o.avatar,
            staffName: o.staffName,
            discount: 0,
            supplyFee: 0,
            tip: 0,
          } as IApiSplitTicketItem)
      );

      // Reset locks và editing flag khi mở modal
      lockedFields.current = [];
      isUserEditingRef.current = false;

      // Rebalance tất cả categories với dữ liệu mới
      const totals = {
        amount: roundToDecimal(details.subTotal),
        tip: roundToDecimal(details.tip),
        discount: roundToDecimal(details.discountTicket),
        supplyFee: roundToDecimal(details.supplyFee || 0),
      };

      const balancedDataList = rebalanceAllCategories(dataList, totals, []);
      form.setFieldValue('staffs', balancedDataList);
      staffsRef.current = balancedDataList;
      setVisible(true);
      setTimeout(() => {
        currencyRef.current?.setValue(formatCurrency(details?.tip || 0));
      }, 100);
    },
  }));

  return (
    <Modal
      modalTitle='Split Master'
      okTitle={'Save'}
      closable={false}
      visible={visible}
      onClose={handleClose}
      width={'auto'}
      disableOk={false}
      noneBodyStyle
      noneFooterStyle
      containerPadding={1}
      className='modal-overflow-unset modal-max-height-unset'
      centered
      footer={null}
      forceRender
      v2={v2}
    >
      <Container className={v2 ? 'v2' : ''}>
        <Form form={form} onFinish={handleFinish}>
          <Form.List name={'staffs'}>
            {(fields, { add }) => {
              // Override add function để sử dụng auto-balance
              const addWithAutoBalance = (
                defaultValue?: IApiSplitTicketItem
              ) => {
                if (defaultValue) {
                  handleAddStaffAutoBalance(defaultValue);
                } else {
                  add();
                }
              };

              // Override remove function để sử dụng auto-balance
              const removeWithAutoBalance = (index: number) => {
                handleRemoveStaffAutoBalance(index);
              };

              return (
                <div className='table-wrapper'>
                  <TableStyled className='table-container'>
                    <thead className='table-thead'>
                      <tr>
                        <th
                          className='staff-info no-style'
                          style={{ background: '#fff' }}
                        />
                        <th>
                          <div className='container-box'>
                            <span>Sub-total</span>
                          </div>
                        </th>
                        <th>
                          <div className='container-box'>
                            <span>Tip</span>
                          </div>
                        </th>
                        <th>
                          <div className='container-box'>
                            <span>Discount</span>
                          </div>
                        </th>
                        <th>
                          <div className='container-box'>
                            <span>Supply Fee</span>
                          </div>
                        </th>
                        <th className='action'>
                          <div className='container-box' />
                        </th>
                      </tr>
                      <tr className='thead-total-row'>
                        <th
                          className='staff-info no-style'
                          style={{ background: '#fff' }}
                        />
                        <th>
                          <div className='container-box'>
                            {formatCurrency(details.subTotal)}
                          </div>
                        </th>
                        <th>
                          <div className='container-box'>
                            {formatCurrency(details.tip)}
                          </div>
                        </th>
                        <th>
                          <div className='container-box'>
                            {formatCurrency(details.discountTicket)}
                          </div>
                        </th>
                        <th>
                          <div className='container-box'>
                            {formatCurrency(details.supplyFee)}
                          </div>
                        </th>
                        <th className='action'>
                          <div className='container-box' />
                        </th>
                      </tr>
                    </thead>
                    <tbody className='table-tbody'>
                      {fields.map((o, i) => (
                        <Form.Item noStyle name={o.name} key={o.key}>
                          <StaffRow
                            name={o.name}
                            remove={() => removeWithAutoBalance(i)}
                            disabledRemove={fields.length === 1}
                            disableDiscount={!details?.discountTicket}
                            disableSupplyFee={!details.supplyFee}
                            onFieldChange={(category, value) =>
                              handleFieldChange(i, category, value)
                            }
                            onFocus={(category) => handleFocus(i, category)}
                            onBlur={handleBlur}
                          />
                        </Form.Item>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className=''>
                        <th
                          className='staff-info'
                          style={{ background: '#fff' }}
                        ></th>
                        <th colSpan={5}>
                          <Button
                            cardCashier={v2}
                            ntype='LIGHT_BLUE'
                            width='100%'
                            onClick={() => {
                              setVisibleChooseStaff(true);
                              addRef.current = addWithAutoBalance;
                            }}
                          >
                            + Add New Staff
                          </Button>
                        </th>
                      </tr>
                    </tfoot>
                  </TableStyled>
                </div>
              );
            }}
          </Form.List>
          <Form.Item shouldUpdate noStyle>
            {({ getFieldsValue }) => {
              const values: FormValues = getFieldsValue();
              const validForm = checkValid(values.staffs);
              return (
                <div className='footer-modal'>
                  <Text
                    style={{
                      opacity: validForm.error ? 1 : 0,
                      fontSize: '1.2rem',
                      fontWeight: '500',
                    }}
                    m={1}
                    textAlign='center'
                    variant='CONTENT_1'
                    color='error_6'
                  >
                    {validForm.error || 'fail'}
                  </Text>
                  <GroupButtonFooterModalStyled>
                    <ButtonFooter
                      buttonName='Close'
                      type='DEFAULT'
                      onClick={() => setVisible(false)}
                      cardCashier={v2}
                    />
                    <ButtonFooter
                      buttonName='Reset'
                      type='SECONDARY'
                      onClick={handleReset}
                      cardCashier={v2}
                    />
                    <ButtonFooter
                      buttonName='Apply'
                      type='PRIMARY'
                      disabled={!validForm.valid || isUserEditingRef.current}
                      onClick={() => form.submit()}
                      cardCashier={v2}
                    />
                  </GroupButtonFooterModalStyled>
                </div>
              );
            }}
          </Form.Item>
        </Form>
      </Container>
      <ModalChooseStaffFilter
        filter={filterStaff}
        visible={visibleChooseStaff}
        onChooseStaff={handleAddStaff}
        onClose={() => setVisibleChooseStaff(false)}
      />
      {modalConfirm}
    </Modal>
  );
});
ModalSplitTicket.displayName = 'ModalSplitTicket';
const useModalSplitTicket = (): [React.RefObject<Ref>, JSX.Element] => {
  const ref = useRef<Ref>(null);
  const component = <ModalSplitTicket ref={ref} />;

  return [ref, component];
};
export default useModalSplitTicket;

const TableStyled = styled.table`
  border-collapse: collapse;
  &.table-container {
    .table-thead {
      tr {
        th {
          background: #e5e6eb;
          padding: 8px;
          &.no-style {
            background: transparent;
          }
          span {
            font-size: 1.2rem;
          }
        }
        &.thead-total-row {
          font-size: 1.5rem;
          .container-box {
            text-align: right;
            justify-content: flex-end;
            padding-right: 12px;
          }
          th {
            height: 48px;
            padding: 0;
          }
        }
      }
      .btn-add {
        background: #6fabb6;
        height: 48px;
        width: 48px;
        border-radius: 8px;
        color: #fff;
        span {
          font-size: 1.5rem;
        }
      }
    }
    th,
    td {
      position: relative;
      z-index: 1;
      min-width: 150px;
      height: 48px;
      &.staff-info {
        min-width: 120px;
      }
      &.action {
        min-width: 48px;
      }
      .container-box {
        position: absolute;
        inset: 0;
        background: transparent;
        border: 2px solid #fff;
        display: flex;
        align-items: center;
        justify-content: center;
        &.with-input {
          justify-content: flex-end;
        }
        &.staff-col {
          justify-content: start;
        }
      }
      &.action {
        padding: 8px !important;
      }
    }
    .table-tbody {
      tr {
        td {
          background: #e5e6eb;
          text-align: right;
          padding: 4px 0px;
          padding-right: 2px;
          &.no-style {
            background: transparent;
          }
          &.staff-info {
            min-width: 100px;
            .CONTENT_2 {
              font-size: 1.2rem;
            }
          }
          .ant-input {
            height: 48px !important;
            width: 80% !important;
            text-align: right;
            font-size: 1.5rem;
            &:disabled {
              // color: #1D2129;
              background: #fff;
            }
          }
        }
      }

      .btn-delete {
        background: #f53f3f;
        height: 48px;
        width: 48px;
        border-radius: 8px;
        color: #fff;
        span {
          font-size: 1.5rem;
        }
      }
    }
  }
`;

const Container = styled.div`
  display: flex;
  flex: 1;
  padding: 1rem;
  .currency-container {
    width: 500px;
  }
  .InputViewStyled {
    justify-content: center;
    input {
      width: 80%;
      flex: unset;
    }
  }
  .footer-modal {
  }
  .table-wrapper {
    max-height: 590px;
    overflow: auto;
    .table-thead {
      position: sticky;
      top: 0;
      z-index: 99;
    }
    tfoot {
      position: sticky;
      bottom: 0;
      z-index: 99;
      tr th {
        background: #fff;
        padding-top: 6px;
        .ant-btn {
          box-shadow: none;
        }
      }
    }
  }

  &.v2 {
    zoom: 1.2;
  }
`;
type StaffRowProps = {
  value?: IApiSplitTicketItem;
  name: any;
  remove?: () => void;
  disabledRemove?: boolean;
  disableSupplyFee?: boolean;
  disableDiscount?: boolean;
  onFieldChange?: (category: CategoryType, value: number) => void;
  onFocus?: (category: CategoryType) => void;
  onBlur?: (category: CategoryType) => void;
};
const StaffRow = ({
  value,
  name,
  remove,
  disabledRemove,
  disableDiscount,
  disableSupplyFee,
  onFieldChange,
  onFocus,
  onBlur,
}: StaffRowProps) => {
  return (
    <tr>
      <td className='staff-info no-style'>
        <div className='container-box staff-col'>
          <Box display='flex' gap='1' alignItems='center'>
            <Avatar src={value?.avatar || 'fail'}>
              {value?.staffName?.[0] || 'A'}
            </Avatar>
            <Text variant='CONTENT_2'>{value?.staffName}</Text>
          </Box>
        </div>
      </td>
      <td>
        <div className='container-box with-input'>
          <Form.Item noStyle name={[name, 'amount']}>
            <CurrencyInputPrecision
              onChangeDebounce={(value) => onFieldChange?.('amount', value)}
              onFocus={() => onFocus?.('amount')}
              onBlur={() => onBlur?.('amount')}
            />
          </Form.Item>
        </div>
      </td>
      <td>
        <div className='container-box with-input'>
          <Form.Item noStyle name={[name, 'tip']}>
            <CurrencyInputPrecision
              onChangeDebounce={(value) => onFieldChange?.('tip', value)}
              onFocus={() => onFocus?.('tip')}
              onBlur={() => onBlur?.('tip')}
            />
          </Form.Item>
        </div>
      </td>
      <td>
        <div className='container-box with-input'>
          <Form.Item noStyle name={[name, 'discount']}>
            <CurrencyInputPrecision
              disabled={disableDiscount}
              onChangeDebounce={(value) => onFieldChange?.('discount', value)}
              onFocus={() => onFocus?.('discount')}
              onBlur={() => onBlur?.('discount')}
            />
          </Form.Item>
        </div>
      </td>
      <td>
        <div className='container-box with-input'>
          <Form.Item noStyle name={[name, 'supplyFee']}>
            <CurrencyInputPrecision
              disabled={disableSupplyFee}
              onChangeDebounce={(value) => onFieldChange?.('supplyFee', value)}
              onFocus={() => onFocus?.('supplyFee')}
              onBlur={() => onBlur?.('supplyFee')}
            />
          </Form.Item>
        </div>
      </td>
      <td className='action'>
        <div className='container-box'>
          {!disabledRemove && (
            <button type='button' className='btn-delete' onClick={remove}>
              <DeleteOutlined />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};
